import { ConversationBuilderLocale } from "@/locales/localeid";
import { CommonEntity } from "@/views/conversation-builder/models/entity.model";
import { CommonIntent } from "@/views/conversation-builder/models/intent.model";
import { getServiceMethodKey, GLServiceBase, HttpMethod, ServerExceptionCode, ServiceRoute } from "commonui";
import { toString } from "lodash";
import {
	ConversationDetailResponseModel,
	IConversationBuilderService,
	UpsertConversationRequestModel,
	VoiceGenStatus
} from ".";
import {
	ConversationPublishResponseModel,
	UpsertConversationResponseModel,
	RemoveExposureTemplateAssociationModel,
	SaveExposureConversationDetailModel,
	ExposureConversationDetailModel,
	MasterConversationDetailResponseModel,
	CopyImageRequestModel,
	SaveExposureConversationResponseModel,
	InteractionStyleModel,
	TestConversationDetailsDto,
	QueryCLURequestModel,
	QueryCLUResponseModel,
	ConversationChatModelV2,
	AddConversationTemplateModel
} from "./model";

class ConversationBuilderServiceClass extends GLServiceBase<any, any> implements IConversationBuilderService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/conversations" };

	errMsgs = (() => {
		const result = {};
		result[getServiceMethodKey(this, HttpMethod.Delete, ServerExceptionCode.ConversationNotFoundException)] =
			ConversationBuilderLocale.ConversationNotFound;

		return result;
	})();

	getConversation(params: {
		contentId?: string;
		exposureId?: string;
		unitId?: string;
	}): Promise<ConversationDetailResponseModel> {
		return this.get("detail", params);
	}

	getCommonIntents(): Promise<CommonIntent[]> {
		return this.get<CommonIntent[]>("templates/commonintents/list").then((intents) => {
			return intents.map((intent) => {
				intent.id = toString(intent.id);
				intent.entityId = intent.entityId ? toString(intent.entityId) : intent.entityId;
				intent.utterances = intent.utterances.map((utterance) => {
					utterance.id = toString(utterance.id);
					return utterance;
				});
				return intent;
			});
		});
	}

	getInteractionStyles(): Promise<InteractionStyleModel[]> {
		return this.get<InteractionStyleModel[]>("templates/interactionstyles/list").then((styles) => {
			return styles;
		});
	}

	getCommonEntities(): Promise<CommonEntity[]> {
		return this.get<CommonEntity[]>("templates/commonentities/list").then((entities) => {
			return entities.map((entity) => {
				entity.id = toString(entity.id);
				entity.values = entity.values.map((value) => {
					value.id = toString(value.id);
					return value;
				});
				return entity;
			});
		});
	}

	getMasterConversations(params: { withMetadata: boolean }) {
		return this.get<MasterConversationDetailResponseModel[]>("templates", { ...params, types: "MasterDialog" });
	}

	saveConversation(model: UpsertConversationRequestModel): Promise<UpsertConversationResponseModel> {
		return this.update("detail", model, undefined);
	}

	createOrTrainConversation(
		conversationId: number,
		isExposureConversation: boolean,
		isTrained: boolean
	): Promise<ConversationPublishResponseModel> {
		var query = `?isTrained=${isTrained}`;
		return this.create("publish" + query, { conversationId, isExposureConversation });
	}

	createOrTrainTestConversation(conversationId: number): Promise<ConversationPublishResponseModel> {
		return this.create("publishTest", { conversationId });
	}

	getTestConversation(conversationId: number): Promise<TestConversationDetailsDto> {
		return this.get("testConversationDetails", { conversationId });
	}

	getPublishStatus(conversationId: number): Promise<number> {
		return this.get("publishstatus/{conversationId}", {
			conversationId
		});
	}

	getExposureConversationDetail(exposureId: any): Promise<ExposureConversationDetailModel> {
		return this.get("exposures/{exposureId}", {
			exposureId
		});
	}

	removeExposureTemplateAssociation(removeDetails: RemoveExposureTemplateAssociationModel) {
		return this.update("removetemplate", removeDetails);
	}

	saveExposureConversation(
		saveDetails: SaveExposureConversationDetailModel
	): Promise<SaveExposureConversationResponseModel> {
		return this.update("exposure", saveDetails);
	}

	deleteConversation(params: {
		conversationId: number;
		contentId?: string;
		exposureId?: string;
		unitId?: number;
		masterId?: number;
	}): Promise<void> {
		return this.delete(params);
	}

	deleteImage(url: string): Promise<void> {
		return this.delete("image", { url }, undefined);
	}

	copyImages(params: CopyImageRequestModel[]): Promise<{ [x: string]: boolean }> {
		return this.create("copyImages", params, undefined);
	}

	isUniqueName(name: string, conversationId?: number): Promise<boolean> {
		return this.get("isUniqueName", { name, conversationId });
	}

	getConversationCompleteStatus(conversationId: number): Promise<boolean> {
		return this.get("completestatus", { conversationId });
	}

	getConversationPublishedStatus(conversationId: number): Promise<boolean> {
		return this.get("publishedstatus", { conversationId });
	}

	getConverationVoiceGenerationStatus(conversationId: number): Promise<VoiceGenStatus | null> {
		return this.get("voiceURLGenerated", { conversationId });
	}

	getConversationVoiceGenerationTime(conversationId: number): Promise<Date | null> {
		return this.get("voicegenerationtime", { conversationId });
	}

	getConversationInfo(id: number): Promise<ConversationChatModelV2> {
		const params = {
			conversationId: id
		};
		const response = this.get("infoCLU", params);
		return response as Promise<ConversationChatModelV2>;
	}

	toggleIsComplete(conversationId: number, isComplete: boolean): Promise<void> {
		return this.update("{conversationId}/toggleiscomplete/{isComplete}", null, { conversationId, isComplete });
	}

	uploadImage(entityId: string | number, entityTypeId: number, pageId: string, metaData: string, conversationName?: string, entityName?: string): Promise<string> {
		var query = `?conversationName=${conversationName}&entityName=${entityName}`;
		return this.create(
			"uploadImage" + query,
			{
				entityId,
				entityTypeId,
				pageId,
				metaData
			},
			undefined
		);
	}
	removeImage(payload: {
		conversationId: number;
		entityTypeId: number;
		entityId: string | number;
		entityName?: string;
	}): Promise<boolean> {
		const { conversationId, entityTypeId, entityId, entityName } = payload;
		const path = `image/conversation/${conversationId}/type/${entityTypeId}/entityId/${entityId}`;
		var query = `?entityName=${entityName}`;
		return this.delete(path + query, undefined, undefined);
	}
	cloneImageReference(payload: {
		nodes: { fromNodeId: string; toNodeId: string; nodeType: number; }[];
	}): Promise<boolean> {
		const { nodes } = payload;
		return this.create(
			`duplicateImages`,
			nodes.map((item) => ({
				oldEntityId: item.fromNodeId,
				newEntityId: item.toNodeId,
				entityTypeId: item.nodeType
			})),
			undefined
		);
	}
	queryCLU(data: QueryCLURequestModel): Promise<QueryCLUResponseModel> {
		return this.create("queryclu", data);
	}
	addConversationTemplate(data: AddConversationTemplateModel): Promise<void> {
		return this.create("addConversationTemplate", data);
	}
}

export const ConversationBuilderService: IConversationBuilderService = new ConversationBuilderServiceClass();
